import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { BtnComponent } from '../../components/button';
import { PageDialogContent, PageDialogFooter } from '../../components/dialog';
import { ExternalGuest } from '../../components/externalGuests';
import { DatePickerFeild } from '../../components/reactHookFormFields/datePicker';
import { InputField } from '../../components/reactHookFormFields/inputField';
import { ReactSelectField } from '../../components/reactHookFormFields/selectField';
import { useGroupChatContext } from '../../context/groupchatContext';
import { sessionDuration } from '../../helpers/utility';
import { useAddProviderAppointmentsMutation, useGetAppointmentsMetaQuery } from '../../services/appointmentService';
import baseApiHelper from '../../services/baseApiHelper';
import { useSendMessageMutation } from '../../services/chatService';
import { useGetPatientContactQuery, useGetVezoRoomsQuery, useGetVezoRoomUserQuery, useSaveSessionMutation } from '../../services/vezoService';
import { PatientContact } from '../appointment/patientContact';

const ScheduleSessionForm = ({closeSessionPopup}) => {

    const groupChatContext = useGroupChatContext();
    const { authData, selectedChat } = useMemo(() => ({
        authData: groupChatContext.authData,
        selectedChat: groupChatContext.selectedChat,
    }));

    const {
        ...formHandler
    } = useForm({defaultValues: {
        eventAppointmentTitle: selectedChat?.patient?.fname+' '+selectedChat?.patient?.lname+' '+moment().format('MM/DD/YYYY hh:mm A'),
        provider: {id: authData.id, name: authData.name, label: authData.name}, duration: sessionDuration[2]
    }});

    const {
        control,
        setValue,
        handleSubmit,
        formState: { errors },
    } = formHandler;

    const [exteralGuests, setExteralGuests] = useState([]);
    const roomVal = useWatch({ control, name: "current_room" });

    const [addProviderAppointment, {isLoading: isSavingAppt}] = useAddProviderAppointmentsMutation();
    const [saveSession, {isLoading: isSavingSession}] = useSaveSessionMutation();
    const [sendMessage] = useSendMessageMutation();
    const {data: patientContact} = useGetPatientContactQuery({pid: selectedChat.pid}, {skip: !authData?.is_provider })
    const {data: vezoAllRooms} = useGetVezoRoomsQuery(null, { skip: !authData?.is_provider });
    const {data: vezoRoomUsers} = useGetVezoRoomUserQuery({telemed_room_id: roomVal?.telemed_room_id, status: 'online'},{ skip: (!authData?.is_provider || !roomVal?.telemed_room_id), refetchOnMountOrArgChange: true })
    const {data: apptMeta} = useGetAppointmentsMetaQuery({pid: selectedChat.pid})

    useEffect(()=>{
        if(apptMeta){
            const telemedType = apptMeta?.visit_types.find((item)=> item.slug.indexOf('telemed') > -1);
            if(telemedType){
                setValue('encounter_type', {...telemedType, label: telemedType.type})
            }
        }
    }, [apptMeta])

    useEffect(() => {
        if(vezoAllRooms && vezoAllRooms.length > 0){
            let roomIndexToUse = vezoAllRooms?.findIndex((check) => (check?.user_role=="owner"));
            if(roomIndexToUse < 0){
                setValue("current_room", {...vezoAllRooms[0], label: vezoAllRooms[0].room_name});
            }
            else {
                setValue("current_room", {...vezoAllRooms[roomIndexToUse], label: vezoAllRooms[roomIndexToUse].room_name});
            }
        }
    }, [vezoAllRooms]);

    const onSubmit = async(data) => {
        
        let postData =  {
            pid: selectedChat?.pid,
            user_id: data.provider?.id ? data.provider?.id : "",
            eventAppointmentTitle: data.eventAppointmentTitle,
            status: "Scheduled",
            conf_call: 'yes',
            notify_user: 0,
            location_id: selectedChat?.location?.id || selectedChat?.location_id,
            encounter_type: data.encounter_type?.id || data.encounter_type?.slug,
            efax_id: "",
            guest_phone: "",
            external_guest: "",
            appointment_type_id: "1",
            changeAppointmentView: "add",
            start_time: moment(data.start_time).format("YYYY-MM-DD HH:mm:ss"),
            duration: data.duration?.value,
            repeat_unit: "days",
            repeat_until: "num_of_appointments",
            provider: data.provider?.label,
            show_provider: 1,
        }
        
        if(exteralGuests){
            let guestPhones = [];
            let guestEmails = []
            exteralGuests.forEach((item)=>{
                if(item.phone){
                    guestPhones.push(item.phone)
                }
                if(item.email){
                    guestEmails.push(item.email)
                }
            })
            postData.external_guest = guestEmails.join(',')
            postData.guest_phone = guestPhones.join(',')
        }
       
        const res = await addProviderAppointment({postData});
        const response = res?.data?.response;
        if(response.code == 0) {
            const sessionPayload = {
                appt_id: response.data?.id,
                encounter_type: postData.encounter_type,
                encounter_type_temp: postData.encounter_type,
                duration: postData.duration,
                datetime: postData.start_time,
                title: postData.eventAppointmentTitle,
                title_changed: "patientSeleted",
                user_id: postData.user_id,
                user_name: postData.provider,
                vezo_id: data.current_room?.vezo_id,
                pid: selectedChat.pid,
                participants: exteralGuests,
                timezone_long: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }

            const tempPatContacts = patientContact.map((item, index)=>{
                const contatct = data.patientContact[index];
                return {...item, ...contatct}
            })
            sessionPayload.patient_contacts = tempPatContacts;

            const sessionResponse = await saveSession(sessionPayload);

            if(sessionResponse.data){
                if(authData?.is_provider){
                    const payload = {pid: selectedChat?.pid, location_id: postData.location_id, message:{type: 'event', event_type: 'appointment', data: {start_time: postData.start_time, provider_name: postData.provider, vezo_id: sessionResponse.data.vezo_id, visit_url: sessionResponse.data.visit_url, visit_key: sessionResponse.data.visit_key}}};
                    sendMessage(payload);
                }
                toast.success("Appointment created successfully!");
                closeSessionPopup();
                baseApiHelper().invalidateTags(["PatientAppointments"]);
            }
        }
        else {
            if(response.code === -1){
                toast.error(response.msg);
            }
            else {
                toast.error("Appointment already exist!");
            }
        }
    };

    return (
        <>
            <PageDialogContent>
                <form id="session_form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <div className="flex py-2">
                        <ReactSelectField
                            title="Selected Room"
                            name="current_room"
                            className="w-[18rem] text-start"
                            control={control}
                            optionData={vezoAllRooms?.map((v, k) => ({
                                ...v,
                                label: v.room_name,
                            }))}
                            errors={errors}
                            rules={{
                                required: {
                                    value: true,
                                },
                            }}
                        />
                    </div>
                    <div className='flex gap-2 py-2'>
                        <InputField
                            title="Title"
                            name="eventAppointmentTitle"
                            control={control}
                            errors={errors}
                            rules={{
                                required: {
                                    value: true,
                                },
                            }}
                        />
                            
                        <div className="w-[50%]" >
                            <DatePickerFeild
                                title="Date & Time"
                                control={control}
                                name="start_time"
                                dateTime={true}
                                defaultValue={new Date()}
                                minDate={new Date()}
                                rules={{
                                    required: {
                                        value: true,
                                    },
                                    validate: {
                                        futureDate: (value) => {
                                            return new Date(value) > new Date() || "Please select a future date and time"; // Validation for future date/time
                                        }
                                    }
                                }}
                                errors={errors}
                            />
                        </div>
                        <ReactSelectField
                            title="Duration"
                            name="duration"
                            control={control}
                            errors={errors}
                            rules={{
                                required: {
                                    value: true,
                                },
                            }}
                            optionData={sessionDuration}
                            menuPlacement={"bottom"}
                        />
                         <InputField
                            title="Patient Name"
                            name="eventAppointmentTitle"
                            value={selectedChat?.patient?.fname+' '+selectedChat?.patient?.lname}
                            control={control}
                            isDisabled={true}
                        />
                    </div>
                    <div className='flex gap-2 py-2'>
                        <div className="w-1/3">
                            <ReactSelectField
                                title="Telemed Provider"
                                name="provider"
                                control={control}
                                optionData={vezoRoomUsers?.map((v, k) => ({
                                    ...v,
                                    label: v.name,
                                }))}
                                rules={{
                                    required: {value: true},
                                }}
                                errors={errors}
                            />
                        </div>
                        <div className="w-1/3">
                            <ReactSelectField
                                title="Visit Type"
                                name="encounter_type"
                                control={control}
                                optionData={apptMeta?.visit_types?.map((v, k) => ({
                                    ...v,
                                    label: v.type,
                                }))}
                                rules={{
                                    required: {value: true},
                                }}
                                errors={errors}
                            />
                        </div>
                        <div className="w-1/3 text-start">
                            <PatientContact formHandler={formHandler} />
                        </div>
                    </div>
                    <div className='py-2'>
                        <ExternalGuest formHandler={formHandler} exteralGuests={exteralGuests} setExteralGuests={setExteralGuests} />
                    </div>
                </form>
            </PageDialogContent>
            <PageDialogFooter>
                <BtnComponent text="Cancel" btnType="default" onClick={closeSessionPopup} />
                <BtnComponent text="Save" btnType='primary_theme' loading={isSavingAppt || isSavingSession} form="session_form" type="submit" />
            </PageDialogFooter>
        </>
    );
};

export default ScheduleSessionForm;